import React, { Suspense, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import NotFoundPage from './pages/NotFoundPage';
import SettingsPage from './pages/SettingsPage';

import { InteractiveSpherePage } from './pages/InteractiveSpherePage';
import MorphParticlePage from './pages/MorphParticlePage';
import ParticleSystemPage from './pages/ParticleSystemPage';
import GPUParticlePage from './pages/GPUParticlePage';
import ErrorOverlay from './components/ui/ErrorOverlay';
const LinesAADemoPage = React.lazy(() => import('./pages/LinesAADemoPage'));
const LinesD3Page = React.lazy(() => import('./pages/LinesD3Page'));
const MorphShapeCatalog = React.lazy(() => import('./pages/MorphShapeCatalog'));
const MorphLayoutsPage = React.lazy(() => import('./pages/MorphLayoutsPage'));
const MorphDebugPage = React.lazy(() => import('./pages/MorphDebugPage'));
const VizLabPage = React.lazy(() => import('./pages/VizLabPage'));
const OnealProductPage = React.lazy(() => import('./pages/OnealProductPage'));
const ArkPage = React.lazy(() => import('./pages/ArkPage'));
const AgentOsPage = React.lazy(() => import('./pages/AgentOsPage'));
const FanCarouselPage = React.lazy(() => import('./pages/FanCarouselPage'));
import { useSampleUIStore } from './stores/useSampleUIStore';
import useGPUParticleStore from './stores/useGPUParticleStore';
import './index.css';

// App.tsx ist jetzt sehr einfach und delegiert alles an den RouterProvider.
// Die Canvas-Logik wird im AppLayout innerhalb des Routers gehandhabt.

// --- Stildefinitionen für Standalone-Seiten ---
const standalonePageStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  width: '100%',
  padding: '2rem',
};

// Lazy load UniversalUIPage
const UniversalUIPage = React.lazy(() =>
  import('./pages/UniversalUIPage').then(({ UniversalUIPage }) => ({
    default: UniversalUIPage,
  }))
);

// Global Error Boundary — shows error + copy button when React crashes
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppCrash]', error.message, info.componentStack);
  }
  render() {
    if (this.state.error) {
      const errorText = `${this.state.error.message}\n\n${this.state.error.stack ?? ''}`;
      return (
        <div style={{ padding: 20, background: '#0a0e1a', color: '#fca5a5', minHeight: '100vh', fontFamily: 'monospace', fontSize: 13 }}>
          <h2 style={{ color: '#ef4444', marginBottom: 12 }}>App Crash</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 16, background: 'rgba(239,68,68,0.1)', padding: 12, borderRadius: 8 }}>
            {errorText}
          </pre>
          <button
            onClick={() => { navigator.clipboard.writeText(errorText).then(() => alert('Copied!')); }}
            style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
          >Copy Error</button>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{ background: '#475569', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 14, cursor: 'pointer', marginLeft: 8 }}
          >Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** the engine/demo switcher stays off the customer-facing carousel (own host + the /fan route) */
const IS_UDO = typeof window !== 'undefined' && window.location.hostname === 'udo.arkturian.com';
const HIDE_NAV = IS_UDO || (typeof window !== 'undefined' && window.location.pathname.startsWith('/fan'));

function App() {
  const meshRef = useRef(null); // Dummy ref for UniversalUIPage test

  return (
    <AppErrorBoundary>
    <Router>
      {/* Produktion Udo-Kiosk (2026-10-03): kein Zahnrad/Fehler-Overlay auf udo.arkturian.com und /fan, nur mit ?admin=1 */}
      {(!HIDE_NAV || new URLSearchParams(window.location.search).get('admin') === '1') && <ErrorOverlay />}
      <div className="App">
        {/* Compact Navigation Dropdown — not on the customer carousel (udo.arkturian.com / /fan, Alex 2026-09-09) */}
        {!HIDE_NAV && <nav style={{
          position: 'fixed', top: '8px', left: '8px', zIndex: 1000,
        }}>
          <select
            onChange={(e) => { if (e.target.value) window.location.href = e.target.value; }}
            defaultValue=""
            style={{
              background: 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.95))',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(139,92,246,0.4)', borderRadius: '10px',
              color: '#e2e8f0', fontSize: '13px', fontWeight: 600, padding: '8px 28px 8px 12px',
              cursor: 'pointer', WebkitAppearance: 'none', appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%270 0 12 12%27%3E%3Cpath d=%27M3 5l3 3 3-3%27 fill=%27%23a78bfa%27/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            <option value="" disabled style={{background: '#1e293b'}}>Arcturian</option>
            <option value="/" style={{background: '#1e293b', color: '#94a3b8'}}>Home</option>
            <option value="/gpu-particle-system" style={{background: '#1e293b', color: '#f87171'}}>GPU System</option>
            <option value="/oneal" style={{background: '#1e293b', color: '#fb923c'}}>O'Neal Products</option>
            <option value="/morph-catalog" style={{background: '#1e293b', color: '#a78bfa'}}>Morph Catalog</option>
            <option value="/morph-layouts" style={{background: '#1e293b', color: '#a78bfa'}}>Morph Layouts</option>
            <option value="/viz-lab" style={{background: '#1e293b', color: '#a78bfa'}}>Viz Lab</option>
            <option value="/morph-particle" style={{background: '#1e293b', color: '#4ade80'}}>Morph Particle</option>
            <option value="/particle-system" style={{background: '#1e293b', color: '#c084fc'}}>CPU System</option>
            <option value="/lines-aa" style={{background: '#1e293b', color: '#38bdf8'}}>Lines AA</option>
            <option value="/lines-d3" style={{background: '#1e293b', color: '#2dd4bf'}}>Lines D3</option>
            <option value="/settings" style={{background: '#1e293b', color: '#64748b'}}>Settings</option>
            <option value="/yaml-ui" style={{background: '#1e293b', color: '#fb923c'}}>YAML UI</option>
          </select>
        </nav>}

        <Routes>
          {/* customer host: the carousel IS the site (no /fan in the URL, Alex 2026-09-09) */}
          <Route path="/" element={IS_UDO ? <Suspense fallback={<div>Loading...</div>}><FanCarouselPage /></Suspense> : <HomePage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/yaml-ui" element={
            <div style={standalonePageStyle}>
              <div style={{width: '100%', maxWidth: '700px'}}>
                <Suspense fallback={<div>Loading...</div>}>
                  <UniversalUIPage yamlFile="sample-ui.yaml" store={useSampleUIStore as any} meshRef={meshRef} />
                </Suspense>
              </div>
            </div>
          } />
          <Route path="/interactive-sphere" element={<InteractiveSpherePage />} />
          <Route path="/morph-particle" element={<MorphParticlePage />} />
          <Route path="/particle-system" element={<ParticleSystemPage />} />
          <Route path="/gpu-particle-system" element={<GPUParticlePage store={useGPUParticleStore} />} />
          <Route path="/lines-aa" element={<Suspense fallback={<div>Loading...</div>}><LinesAADemoPage /></Suspense>} />
          <Route path="/lines-d3" element={<Suspense fallback={<div>Loading...</div>}><LinesD3Page /></Suspense>} />
          <Route path="/morph-catalog" element={<Suspense fallback={<div>Loading...</div>}><MorphShapeCatalog /></Suspense>} />
          <Route path="/morph-layouts" element={<Suspense fallback={<div>Loading...</div>}><MorphLayoutsPage /></Suspense>} />
          <Route path="/morph-debug" element={<Suspense fallback={<div>Loading...</div>}><MorphDebugPage /></Suspense>} />
          <Route path="/viz-lab" element={<Suspense fallback={<div>Loading...</div>}><VizLabPage /></Suspense>} />
          <Route path="/oneal" element={<Suspense fallback={<div>Loading...</div>}><OnealProductPage /></Suspense>} />
          <Route path="/ark" element={<Suspense fallback={<div>Loading...</div>}><ArkPage /></Suspense>} />
          <Route path="/agentos" element={<Suspense fallback={<div>Loading...</div>}><AgentOsPage /></Suspense>} />
          <Route path="/fan" element={<Suspense fallback={<div>Loading...</div>}><FanCarouselPage /></Suspense>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
    </AppErrorBoundary>
  );
}

export default App; 